import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import AuthLayout from '@/components/layout/AuthLayout'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  })
  const [loading] = useState(false)
  const [error, setError] = useState('')
  
  const { signUp, loading: authLoading, error: authError, clearError } = useAuth()
  const navigate = useNavigate()

  const validatePassword = (password: string) => {
    const requirements = [
      { test: /.{8,}/, message: 'At least 8 characters' },
      { test: /[a-z]/, message: 'One lowercase letter' },
      { test: /[A-Z]/, message: 'One uppercase letter' },
      { test: /\d/, message: 'One number' },
      { test: /[@$!%*?&]/, message: 'One special character (@$!%*?&)' }
    ]
    
    return requirements.filter(req => !req.test.test(password)).map(req => req.message)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    clearError()

    // Validate required fields
    if (!formData.firstName.trim()) {
      setError('First name is required')
      return
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required')
      return
    }
    if (!formData.email.trim()) {
      setError('Email is required')
      return
    }

    // Validate password requirements
    const passwordErrors = validatePassword(formData.password)
    if (passwordErrors.length > 0) {
      setError(`Password must contain: ${passwordErrors.join(', ')}`)
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    try {
      await signUp({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName
      })
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setError('Registration failed. Please try again.')
    }
  }

  // Use auth store loading state
  const isLoading = loading || authLoading

  // Show auth store error or local error
  const displayError = error || authError

  const updateFormData = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <AuthLayout 
      title="Create Account" 
      subtitle="Fill in your information to get started"
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
        {displayError && (
          <div style={{ 
            backgroundColor: '#FEF2F2', 
            border: '1px solid #FECACA', 
            borderRadius: '8px', 
            padding: '12px',
            marginBottom: '16px'
          }}>
            <p style={{ fontSize: '14px', color: 'var(--danger-color)', textAlign: 'center', margin: 0 }}>
              {displayError}
            </p>
          </div>
        )}
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Input
            label="First Name"
            type="text"
            value={formData.firstName}
            onChange={(e) => updateFormData('firstName', e.target.value)}
            required
            autoComplete="given-name"
          />
          
          <Input
            label="Last Name"
            type="text"
            value={formData.lastName}
            onChange={(e) => updateFormData('lastName', e.target.value)}
            required
            autoComplete="family-name"
          />
        </div>
        
        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => updateFormData('email', e.target.value)}
          required
          autoComplete="email"
        />
        
        <div>
          <Input
            label="Password"
            type="password"
            value={formData.password}
            onChange={(e) => updateFormData('password', e.target.value)}
            required
            autoComplete="new-password"
          />
          <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-muted)' }}>
            Password must contain:
            <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
              <li>At least 8 characters</li>
              <li>One lowercase letter</li>
              <li>One uppercase letter</li>
              <li>One number</li>
              <li>One special character (@$!%*?&)</li>
            </ul>
          </div>
        </div>
        
        <Input
          label="Confirm Password"
          type="password"
          value={formData.confirmPassword}
          onChange={(e) => updateFormData('confirmPassword', e.target.value)}
          required
          autoComplete="new-password"
        />
        
        <Button
          type="submit"
          className="w-full"
          loading={isLoading}
        >
          Create Account
        </Button>
        
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', paddingTop: '16px' }}>
          <span style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
            Already have an account?{' '}
          </span>
          <Link 
            to="/login" 
            className="link-text"
            style={{ fontSize: '14px' }}
          >
            Sign in
          </Link>
        </div>
      </form>
    </AuthLayout>
  )
}